import { create } from 'zustand';
import { devtools, persist } from 'zustand/middleware';

export type AppNotificationType = 'partial' | 'active' | 'error';

export interface AppNotification {
  id: string;
  type: AppNotificationType;
  videoId: string;
  videoTitle: string;
  message: string;
  read: boolean;
  createdAt: string;
}

interface NotificationsState {
  // Notificações de processamento de vídeo
  notifications: AppNotification[];
  addNotification: (notification: Omit<AppNotification, 'id' | 'read' | 'createdAt'>) => void;
  clearAll: () => void;
  getUnreadCount: () => number;

  // Controle de leitura das novidades do painel
  readIds: string[];
  markAsRead: (id: string) => void;
  markAllAsRead: (ids: string[]) => void;
  isRead: (id: string) => boolean;
}

export const useNotificationsStore = create<NotificationsState>()(
  devtools(
    persist(
      (set, get) => ({
        notifications: [],

        addNotification: (notification) => {
          const { notifications } = get();

          // Evitar duplicatas: não re-adicionar notificação do mesmo vídeo + tipo
          const alreadyExists = notifications.some(
            (n) => n.videoId === notification.videoId && n.type === notification.type
          );
          if (alreadyExists) return;

          const newNotification: AppNotification = {
            ...notification,
            id: `${notification.videoId}_${notification.type}_${Date.now()}`,
            read: false,
            createdAt: new Date().toISOString(),
          };

          set({ notifications: [newNotification, ...notifications] });
        },

        clearAll: () => {
          set({ notifications: [] });
        },

        getUnreadCount: () => {
          return get().notifications.filter((n) => !n.read).length;
        },

        // Novidades read tracking
        readIds: [],

        markAsRead: (id) =>
          set((state) => ({
            readIds: state.readIds.includes(id) ? state.readIds : [...state.readIds, id],
          })),

        markAllAsRead: (ids) =>
          set((state) => ({
            readIds: [...new Set([...state.readIds, ...ids])],
          })),

        isRead: (id) => get().readIds.includes(id),
      }),
      {
        name: 'hostvsl-app-notifications',
        partialize: (state) => ({
          notifications: state.notifications.slice(0, 50),
          readIds: state.readIds,
        }),
      }
    )
  )
);
